'use client';

import { KeyboardEvent, PointerEvent, useMemo, useRef } from 'react';

type ColorPickerProps = {
  value: string;
  onChange: (color: string) => void;
};

type Hsv = {
  h: number;
  s: number;
  v: number;
};

function clamp(value: number, min = 0, max = 1) {
  return Math.min(Math.max(value, min), max);
}

function componentToHex(value: number) {
  return Math.round(value).toString(16).padStart(2, '0');
}

function hsvToRgb({ h, s, v }: Hsv) {
  const chroma = v * s;
  const huePart = h / 60;
  const x = chroma * (1 - Math.abs((huePart % 2) - 1));
  const match = v - chroma;
  let red = 0;
  let green = 0;
  let blue = 0;

  if (huePart >= 0 && huePart < 1) {
    red = chroma;
    green = x;
  } else if (huePart < 2) {
    red = x;
    green = chroma;
  } else if (huePart < 3) {
    green = chroma;
    blue = x;
  } else if (huePart < 4) {
    green = x;
    blue = chroma;
  } else if (huePart < 5) {
    red = x;
    blue = chroma;
  } else {
    red = chroma;
    blue = x;
  }

  return {
    r: (red + match) * 255,
    g: (green + match) * 255,
    b: (blue + match) * 255,
  };
}

function hsvToHex(hsv: Hsv) {
  const { r, g, b } = hsvToRgb(hsv);
  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
}

function hexToHsv(hex: string): Hsv {
  const normalized = /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : '#60a5fa';
  const r = parseInt(normalized.slice(1, 3), 16) / 255;
  const g = parseInt(normalized.slice(3, 5), 16) / 255;
  const b = parseInt(normalized.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let h = 0;

  if (delta !== 0) {
    if (max === r) {
      h = 60 * (((g - b) / delta) % 6);
    } else if (max === g) {
      h = 60 * ((b - r) / delta + 2);
    } else {
      h = 60 * ((r - g) / delta + 4);
    }
  }

  return {
    h: h < 0 ? h + 360 : h,
    s: max === 0 ? 0 : delta / max,
    v: max,
  };
}

export default function ColorPicker({ value, onChange }: ColorPickerProps) {
  const hueRef = useRef<HTMLDivElement | null>(null);
  const hsv = useMemo(() => hexToHsv(value), [value]);
  const hueColor = hsvToHex({ h: hsv.h, s: 1, v: 1 });

  const updateHue = (nextHue: number) => {
    const normalizedHue = ((nextHue % 360) + 360) % 360;
    onChange(hsvToHex({ h: normalizedHue, s: 1, v: 1 }));
  };

  const updateFromHue = (event: PointerEvent<HTMLDivElement>) => {
    const rect = hueRef.current?.getBoundingClientRect();
    if (!rect) return;
    const nextHue = clamp((event.clientX - rect.left) / rect.width) * 360;
    updateHue(nextHue);
  };

  const handleHueKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 30 : 5;
    const keyActions: Record<string, number> = {
      ArrowLeft: hsv.h - step,
      ArrowDown: hsv.h - step,
      ArrowRight: hsv.h + step,
      ArrowUp: hsv.h + step,
      Home: 0,
      End: 359,
    };
    const nextHue = keyActions[event.key];

    if (nextHue === undefined) return;
    event.preventDefault();
    updateHue(nextHue);
  };

  return (
    <div className="flex items-center gap-3">
      <span className="h-10 w-10 shrink-0 rounded-full border-2 border-white shadow-none" style={{ backgroundColor: hueColor }} />
      <div
        ref={hueRef}
        role="slider"
        tabIndex={0}
        aria-label="Choisir la teinte"
        aria-valuemin={0}
        aria-valuemax={360}
        aria-valuenow={Math.round(hsv.h)}
        aria-valuetext={`${Math.round(hsv.h)} degres`}
        onKeyDown={handleHueKeyDown}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          updateFromHue(event);
        }}
        onPointerMove={(event) => {
          if (event.buttons === 1) updateFromHue(event);
        }}
        className="relative h-4 flex-1 touch-none rounded-full border border-slate-700 shadow-none"
        style={{
          background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
        }}
      >
        <span
          className="absolute top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-white shadow-[0_0_0_1px_rgba(15,23,42,0.6)]"
          style={{ left: `${(hsv.h / 360) * 100}%`, backgroundColor: hueColor }}
        />
      </div>
    </div>
  );
}
